import { RootState } from "Realty/store";
import { createSelector } from "@reduxjs/toolkit";

export const selectAdmin = (state: RootState) => state.adminReducer;

//
// uploadFiles
//

export const selectUploadFiles = (state: RootState) =>
    selectAdmin(state)?.uploadFiles ?? {
        pending: false,
        error: false,
        errorMessage: null,
        paths: [],
    };

export const selectUploadFilesPending = createSelector(
    [selectUploadFiles],
    (s) => !!s.pending,
);

export const selectUploadFilesError = createSelector(
    [selectUploadFiles],
    (s) => !!s.error,
);

export const selectUploadFilesErrorMessage = createSelector(
    [selectUploadFiles],
    (s) => s.errorMessage ?? null,
);

export const selectUploadFilesPaths = createSelector(
    [selectUploadFiles],
    (s) => s.paths ?? [],
);

//
// deleteUser
//

export const selectDeleteUserState = (state: RootState) =>
    selectAdmin(state)?.deleteUser ?? {
        pending: false,
        error: false,
        errorMessage: null,
        userId: null,
    };

export const selectDeleteUserPending = createSelector(
    [selectDeleteUserState],
    (s) => !!s.pending,
);

export const selectDeleteUserError = createSelector(
    [selectDeleteUserState],
    (s) => !!s.error,
);

export const selectDeleteUserErrorMessage = createSelector(
    [selectDeleteUserState],
    (s) => s.errorMessage ?? null,
);

export const selectDeletingUserId = createSelector(
    [selectDeleteUserState],
    (s) => s.userId ?? null,
);

export const makeSelectIsDeletingUser = (userId: number | string) =>
    createSelector(
        [selectDeletingUserId, selectDeleteUserPending],
        (currentId, pending) => {
            if (!pending) return false;
            if (currentId == null) return false;
            return String(currentId) === String(userId);
        },
    );

//
// subscribedUsers
//

export const selectSubscribedUsersState = (state: RootState) =>
    selectAdmin(state)?.subscribedUsers ?? {
        pending: false,
        error: false,
        errorMessage: null,
        users: [],
        total: null,
        page: 1,
        perPage: 50,
        lastPage: null,
        nextUrl: null,
        prevUrl: null,
    };

export const selectSubscribedUsersPending = createSelector(
    [selectSubscribedUsersState],
    (s) => !!s.pending,
);

export const selectSubscribedUsersError = createSelector(
    [selectSubscribedUsersState],
    (s) => !!s.error,
);

export const selectSubscribedUsersErrorMessage = createSelector(
    [selectSubscribedUsersState],
    (s) => s.errorMessage ?? null,
);

export const selectSubscribedUsersList = createSelector(
    [selectSubscribedUsersState],
    (s) => (Array.isArray(s.users) ? s.users : []),
);

export const selectSubscribedUsersTotal = createSelector(
    [selectSubscribedUsersState],
    (s) => (typeof s.total === "number" ? s.total : null),
);

export const selectSubscribedUsersPage = createSelector(
    [selectSubscribedUsersState],
    (s) => s.page ?? 1,
);

export const selectSubscribedUsersPerPage = createSelector(
    [selectSubscribedUsersState],
    (s) => s.perPage ?? 50,
);

export const selectSubscribedUsersLastPage = createSelector(
    [selectSubscribedUsersState],
    (s) => s.lastPage ?? null,
);

export const selectSubscribedUsersNextUrl = createSelector(
    [selectSubscribedUsersState],
    (s) => s.nextUrl ?? null,
);

export const selectSubscribedUsersPrevUrl = createSelector(
    [selectSubscribedUsersState],
    (s) => s.prevUrl ?? null,
);

//
// users
//

export const selectUsersState = (state: RootState) =>
    selectAdmin(state)?.users ?? {
        pending: false,
        error: false,
        errorMessage: null,
        users: [],
        total: null,
        page: 1,
        perPage: 50,
        lastPage: null,
        nextUrl: null,
        prevUrl: null,
        query: {
            search: "",
            sort: null,
            order: null,
        },
    };

export const selectUsersPending = createSelector([selectUsersState], (s) => !!s.pending);

export const selectUsersError = createSelector([selectUsersState], (s) => !!s.error);

export const selectUsersErrorMessage = createSelector(
    [selectUsersState],
    (s) => s.errorMessage ?? null,
);

export const selectUsersList = createSelector(
    [selectUsersState],
    (s) => (Array.isArray(s.users) ? s.users : []),
);

export const selectUsersTotal = createSelector(
    [selectUsersState],
    (s) => (typeof s.total === "number" ? s.total : null),
);

export const selectUsersPage = createSelector([selectUsersState], (s) => s.page ?? 1);

export const selectUsersPerPage = createSelector([selectUsersState], (s) => s.perPage ?? 50);

export const selectUsersLastPage = createSelector([selectUsersState], (s) => s.lastPage ?? null);

export const selectUsersNextUrl = createSelector([selectUsersState], (s) => s.nextUrl ?? null);

export const selectUsersPrevUrl = createSelector([selectUsersState], (s) => s.prevUrl ?? null);

//
// query
//

export const selectUsersSearch = createSelector(
    [selectUsersState],
    (s) => s.query?.search ?? "",
);

export const selectUsersSort = createSelector([selectUsersState], (s) => s.query?.sort ?? null);

export const selectUsersOrder = createSelector([selectUsersState], (s) => s.query?.order ?? null);

//
// setUserSubscription
//

export const selectSetUserSubscriptionState = (state: RootState) =>
    selectAdmin(state)?.setUserSubscription ?? {
        pending: false,
        error: false,
        errorMessage: null,
        userId: null,
        planId: null,
        planCode: null,
        expiresAt: null,
    };

export const selectSetUserSubscriptionPending = createSelector(
    [selectSetUserSubscriptionState],
    (s) => !!s.pending,
);

export const selectSetUserSubscriptionError = createSelector(
    [selectSetUserSubscriptionState],
    (s) => !!s.error,
);

export const selectSetUserSubscriptionErrorMessage = createSelector(
    [selectSetUserSubscriptionState],
    (s) => s.errorMessage ?? null,
);

export const selectSetUserSubscriptionUserId = createSelector(
    [selectSetUserSubscriptionState],
    (s) => s.userId ?? null,
);

export const selectSetUserSubscriptionPlanId = createSelector(
    [selectSetUserSubscriptionState],
    (s) => s.planId ?? null,
);

export const selectSetUserSubscriptionPlanCode = createSelector(
    [selectSetUserSubscriptionState],
    (s) => s.planCode ?? null,
);

export const selectSetUserSubscriptionExpiresAt = createSelector(
    [selectSetUserSubscriptionState],
    (s) => s.expiresAt ?? null,
);

/**
 * Проверяет, оформляется ли подписка конкретному пользователю (для дизейбла кнопок в строке)
 */
export const makeSelectIsSettingSubscriptionForUser = (userId: number | string) =>
    createSelector(
        [selectSetUserSubscriptionUserId, selectSetUserSubscriptionPending],
        (currentId, pending) => {
            if (!pending) return false;
            if (currentId == null) return false;
            return String(currentId) === String(userId);
        },
    );

//
// cancelUserSubscription
//

export const selectCancelUserSubscriptionState = (state: RootState) =>
    selectAdmin(state)?.cancelUserSubscription ?? {
        pending: false,
        error: false,
        errorMessage: null,
        userId: null,
    };

export const selectCancelUserSubscriptionPending = createSelector(
    [selectCancelUserSubscriptionState],
    (s) => !!s.pending,
);

export const selectCancelUserSubscriptionError = createSelector(
    [selectCancelUserSubscriptionState],
    (s) => !!s.error,
);

export const selectCancelUserSubscriptionErrorMessage = createSelector(
    [selectCancelUserSubscriptionState],
    (s) => s.errorMessage ?? null,
);

export const selectCancelUserSubscriptionUserId = createSelector(
    [selectCancelUserSubscriptionState],
    (s) => s.userId ?? null,
);

/**
 * Проверяет, отменяется ли подписка у конкретного пользователя
 */
export const makeSelectIsCancelingSubscriptionForUser = (userId: number | string) =>
    createSelector(
        [selectCancelUserSubscriptionUserId, selectCancelUserSubscriptionPending],
        (currentId, pending) => {
            if (!pending) return false;
            if (currentId == null) return false;
            return String(currentId) === String(userId);
        },
    );

//////////////////////////

//
// updateUser
//

export const selectUpdateUserState = (state: RootState) =>
    selectAdmin(state)?.updateUser ?? {
        pending: false,
        error: false,
        errorMessage: null,
        userId: null,
    };

export const selectUpdateUserPending = createSelector(
    [selectUpdateUserState],
    (s) => !!s.pending,
);

export const selectUpdateUserError = createSelector(
    [selectUpdateUserState],
    (s) => !!s.error,
);

export const selectUpdateUserErrorMessage = createSelector(
    [selectUpdateUserState],
    (s) => s.errorMessage ?? null,
);

export const selectUpdatingUserId = createSelector(
    [selectUpdateUserState],
    (s) => s.userId ?? null,
);

/**
 * Проверяет, обновляются ли данные конкретного пользователя
 */
export const makeSelectIsUpdatingUser = (userId: number | string) =>
    createSelector(
        [selectUpdatingUserId, selectUpdateUserPending],
        (currentId, pending) => {
            if (!pending) return false;
            if (currentId == null) return false;
            return String(currentId) === String(userId);
        },
    );

//
// resetUserPassword
//

export const selectResetUserPasswordState = (state: RootState) =>
    selectAdmin(state)?.resetUserPassword ?? {
        pending: false,
        error: false,
        errorMessage: null,
        email: null,
    };

export const selectResetUserPasswordPending = createSelector(
    [selectResetUserPasswordState],
    (s) => !!s.pending,
);

export const selectResetUserPasswordError = createSelector(
    [selectResetUserPasswordState],
    (s) => !!s.error,
);

export const selectResetUserPasswordErrorMessage = createSelector(
    [selectResetUserPasswordState],
    (s) => s.errorMessage ?? null,
);

export const selectResetUserPasswordEmail = createSelector(
    [selectResetUserPasswordState],
    (s) => s.email ?? null,
);

/**
 * Проверяет, отправляется ли сброс пароля на конкретный email (для дизейбла кнопки в строке)
 */
export const makeSelectIsResettingPasswordForEmail = (email: string) =>
    createSelector(
        [selectResetUserPasswordEmail, selectResetUserPasswordPending],
        (currentEmail, pending) => {
            if (!pending) return false;
            if (!currentEmail) return false;
            return String(currentEmail).toLowerCase() === String(email).trim().toLowerCase();
        },
    );
